import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowUpRight,
  Barbell,
  CircleNotch,
  ClockCountdown,
  FireSimple,
  HouseSimple,
  InstagramLogo,
  MapPin,
  Phone,
  Sparkle,
  UsersThree,
  WarningCircle,
  WhatsappLogo,
} from "@phosphor-icons/react";

import { MembershipInquiryForm } from "./MembershipInquiryForm";
import { pulseContent } from "../data/pulseContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PULSE_MAP_LINK = "https://www.google.com/maps/place/Pulse+Fitness/@24.7880542,67.0806193,18.77z/data=!4m6!3m5!1s0x3eb33d43597101e9:0xa382d7bf6fbc03b8!8m2!3d24.7880001!4d67.0815796!16s%2Fg%2F11h035q1wg?entry=ttu&g_ep=EgoyMDI2MDQyMi4wIKXMDSoASAFQAw%3D%3D";
const toKebabCase = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const PULSE_INSTAGRAM_LINK = "https://www.instagram.com/pulsefitnesspk";

const revealVariant = {
  hidden: { opacity: 0, y: 36, filter: "blur(12px)" },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.75, delay, ease: [0.2, 0.65, 0.2, 1] },
  }),
};

const RevealBlock = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    custom={delay}
    initial="hidden"
    viewport={{ once: true, amount: 0.2 }}
    whileInView="visible"
    variants={revealVariant}
  >
    {children}
  </motion.div>
);

const SectionIntro = ({ eyebrow, title, body, testId }) => (
  <RevealBlock className="max-w-2xl space-y-4" delay={0.1}>
    <p className="eyebrow" data-testid={`${testId}-eyebrow`}>{eyebrow}</p>
    <h2 className="section-title" data-testid={`${testId}-title`}>{title}</h2>
    <p className="section-copy" data-testid={`${testId}-body`}>{body}</p>
  </RevealBlock>
);

export const SiteHeader = ({ navItems }) => {
  const location = useLocation();
  const onHomePage = location.pathname === "/";

  const renderNavItem = (item) => {
    if (item.href.startsWith("/")) {
      return (
        <Link
          className="nav-link"
          data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          key={item.href}
          to={item.href}
        >
          {item.label}
        </Link>
      );
    }

    if (onHomePage) {
      return (
        <a
          className="nav-link"
          data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          href={item.href}
          key={item.href}
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link
        className="nav-link"
        data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        key={item.href}
        to={`/${item.href}`}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <header className="site-header" data-testid="site-header">
      <div className="shell flex items-center justify-between gap-6 py-5">
        <Link className="brand-mark" data-testid="site-brand-link" to="/">
        <span className="brand-dot" />
        <span>Pulse Fitness</span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="site-navigation">
          {navItems.map((item) => renderNavItem(item))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <Link
            className="pulse-button-outline rounded-full px-6 py-3 text-sm"
            data-testid="header-coaches-button"
            to="/coaches"
          >
            Meet the Coaches
          </Link>

          {onHomePage ? (
            <a
              className="pulse-button-outline rounded-full px-6 py-3 text-sm"
              data-testid="header-membership-button"
              href="#membership"
            >
              Join the club
            </a>
          ) : (
            <Link
              className="pulse-button-outline rounded-full px-6 py-3 text-sm"
              data-testid="header-membership-button"
              to="/#membership"
            >
              Join the club
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export const MobileBottomBar = () => {
  const location = useLocation();
  const onHomePage = location.pathname === "/";
  const onCoachesPage = location.pathname === "/coaches";

  return (
    <div className="mobile-bottom-bar md:hidden" data-testid="mobile-bottom-bar">
      <div className="mobile-bottom-bar-inner">
        {onHomePage ? (
          <a
            className="mobile-bottom-button mobile-bottom-button-active"
            data-testid="mobile-bottom-home-button"
            href="#top"
          >
            <HouseSimple className="h-4 w-4" />
            <span>Home</span>
          </a>
        ) : (
          <Link
            className="mobile-bottom-button"
            data-testid="mobile-bottom-home-button"
            to="/"
          >
            <HouseSimple className="h-4 w-4" />
            <span>Home</span>
          </Link>
        )}

        {onHomePage ? (
          <a
            className="mobile-bottom-button mobile-bottom-button-primary"
            data-testid="mobile-bottom-join-button"
            href="#membership"
          >
            <Sparkle className="h-4 w-4" />
            <span>Join</span>
          </a>
        ) : (
          <Link
            className="mobile-bottom-button mobile-bottom-button-primary"
            data-testid="mobile-bottom-join-button"
            to="/#membership"
          >
            <Sparkle className="h-4 w-4" />
            <span>Join</span>
          </Link>
        )}

        {onCoachesPage ? (
          <div
            className="mobile-bottom-button mobile-bottom-button-active"
            data-testid="mobile-bottom-coaches-button"
          >
            <UsersThree className="h-4 w-4" />
            <span>Coaches</span>
          </div>
        ) : (
          <Link
            className="mobile-bottom-button"
            data-testid="mobile-bottom-coaches-button"
            to="/coaches"
          >
            <UsersThree className="h-4 w-4" />
            <span>Coaches</span>
          </Link>
        )}
      </div>
    </div>
  );
};

const HeroStatus = ({ status }) => {
  if (status === "ready") {
    return null;
  }

  if (status === "loading") {
    return (
      <div className="status-chip" data-testid="hero-live-status-loading">
        <CircleNotch className="h-4 w-4 animate-spin text-[#2D5A5A]" />
        <span>Loading live club details...</span>
      </div>
    );
  }

  return (
    <div className="status-chip status-chip-warning" data-testid="hero-live-status-error">
      <WarningCircle className="h-4 w-4 text-[#B85C5C]" />
      <span>Live club details are temporarily unavailable — showing the premium preview experience.</span>
    </div>
  );
};

const HeroSection = ({ clubInfo, status }) => (
  <section className="hero-shell" data-testid="hero-section" id="top">
    <div className="grain-overlay" />
    <div className="shell grid items-center gap-12 pb-16 pt-32 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20 lg:pb-24 lg:pt-40">
      <RevealBlock className="space-y-8" delay={0.05}>
        <p className="eyebrow" data-testid="hero-eyebrow">Phase 8 DHA • Karachi</p>
        <div className="space-y-6">
          <p className="hero-accolade" data-testid="hero-accolade">
            Rated As Best Gym In DHA, Karachi
          </p>
          <h1 className="hero-title" data-testid="hero-title">
            A Fitness experience built for elite performance.
          </h1>
          <p className="hero-copy" data-testid="hero-copy">
            {clubInfo.hero_tagline} Explore the club through layered visuals, cinematic depth, and a premium membership journey.
          </p>
        </div>

        <HeroStatus status={status} />

        <div className="flex flex-wrap gap-4">
          <a
            className="pulse-button-primary rounded-full px-7 py-4"
            data-testid="hero-membership-button"
            href="#membership"
          >
            Request membership
          </a>
          <a
            className="pulse-button-outline rounded-full px-7 py-4"
            data-testid="hero-tour-button"
            href="#tour"
          >
            Explore 3D tour
          </a>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          {clubInfo.stats.map((stat) => (
            <div className="floating-panel p-5" data-testid={`hero-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`} key={stat.label}>
              <p className="text-3xl font-semibold tracking-tight text-[#1A2525]">{stat.value}</p>
              <p className="mt-2 text-xs tracking-[0.22em] text-[#758A8A]">{stat.label}</p>
            </div>
          ))}
        </div>
      </RevealBlock>

      <RevealBlock className="hero-stage" delay={0.15}>
        <motion.div className="hero-card hero-card-main" animate={{ y: [0, -12, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}>
          <img
            alt="Pulse Fitness luxury gym interior"
            className="hero-image"
            data-testid="hero-main-image"
            src={pulseContent.media.hero}
          />
        </motion.div>

        <motion.div className="hero-card hero-card-secondary floating-panel" animate={{ y: [0, 10, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}>
          <p className="eyebrow mb-3" data-testid="hero-location-tag">Karachi flagship</p>
          <a
            className="whitespace-pre-line text-lg text-[#1A2525] transition-colors duration-300 hover:text-[#2D5A5A]"
            data-testid="hero-location-link"
            href={PULSE_MAP_LINK}
            rel="noreferrer"
            target="_blank"
          >
            {clubInfo.location}
          </a>
          <p className="mt-4 text-sm text-[#4A5D5D]" data-testid="hero-hours-copy">{clubInfo.hours}</p>
        </motion.div>

        <motion.div className="hero-card hero-card-tertiary" animate={{ y: [0, -8, 0], rotate: [0, 1.5, 0] }} transition={{ duration: 6.4, repeat: Infinity, ease: "easeInOut" }}>
          <img
            alt="Pulse Fitness strength floor"
            className="hero-image"
            data-testid="hero-secondary-image"
            src={pulseContent.media.tourPrimary}
          />
        </motion.div>
      </RevealBlock>
    </div>
  </section>
);

const TourSection = ({ frames }) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const topMotion = useTransform(scrollYProgress, [0, 1], [50, -60]);
  const bottomMotion = useTransform(scrollYProgress, [0, 1], [-20, 90]);

  return (
    <section className="section-shell" data-testid="tour-section" id="tour" ref={sectionRef}>
      <div className="shell grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-14">
        <SectionIntro
          body="Browse through a visual walkthrough of the club and experience the space, layout, and atmosphere before your visit. 🏋️‍♂️✨"
          eyebrow="Immersive visual tour"
          testId="tour-section-intro"
          title="Step into the world of Pulse Fitness and experience its premium environment from multiple dynamic angles."
        />

        <div className="mobile-swipe-row grid gap-6 md:grid-cols-2">
          {frames.map((frame, index) => (
            <motion.article
              className="tour-card"
              data-testid={`tour-card-${frame.slug}`}
              key={frame.slug}
              style={{ y: index % 2 === 0 ? topMotion : bottomMotion }}
            >
              {frame.gallery?.length ? (
                <div className="tour-gallery-grid">
                  {frame.gallery.map((image, imageIndex) => (
                    <div className="tour-image-wrap" key={`${frame.slug}-${imageIndex}`}>
                      <img
                        alt={`${frame.title} view ${imageIndex + 1}`}
                        className="tour-image"
                        data-testid={`tour-image-${frame.slug}-${imageIndex + 1}`}
                        src={image}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="tour-image-wrap">
                  <img
                    alt={frame.title}
                    className={`tour-image ${frame.slug === "arrival-lounge" ? "tour-image-contain" : ""}`}
                    data-testid={`tour-image-${frame.slug}`}
                    src={frame.image}
                  />
                </div>
              )}
              <div className="space-y-3 p-6">
                <p className="eyebrow" data-testid={`tour-card-tag-${frame.slug}`}>3D scene {index + 1}</p>
                <h3 className="text-2xl font-semibold tracking-tight text-[#1A2525]" data-testid={`tour-card-title-${frame.slug}`}>{frame.title}</h3>
                <p className="section-copy text-sm" data-testid={`tour-card-description-${frame.slug}`}>{frame.description}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

const ClassesSection = ({ classes }) => (
  <section className="section-shell pt-0" data-testid="classes-section" id="classes">
    <div className="shell space-y-10">
      <SectionIntro
        body="Programs at Pulse Fitness are built to feel sharp, premium, and results-driven — from first session to advanced progression."
        eyebrow="Signature classes"
        testId="classes-section-intro"
        title="Train with structure, intensity, and a luxury club atmosphere."
      />

      <div className="mobile-swipe-row grid gap-6 lg:grid-cols-3">
        {classes.map((item, index) => (
          <RevealBlock className="class-card" delay={index * 0.1} key={item.slug || item.name}>
            <div className="class-image-wrap">
              <img alt={item.name} className="class-image" data-testid={`class-image-${(item.slug || item.name).toLowerCase().replace(/\s+/g, "-")}`} src={item.image || pulseContent.media.classOne} />
            </div>
            <div className="space-y-4 p-6">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-2xl font-semibold tracking-tight text-[#1A2525]" data-testid={`class-title-${(item.slug || item.name).toLowerCase().replace(/\s+/g, "-")}`}>{item.name}</h3>
                <Barbell className="h-6 w-6 text-[#2D5A5A]" />
              </div>
              <p className="section-copy text-sm" data-testid={`class-focus-${(item.slug || item.name).toLowerCase().replace(/\s+/g, "-")}`}>{item.focus}</p>
              <div className="flex flex-wrap gap-3 text-xs tracking-[0.18em] text-[#758A8A]">
                <span data-testid={`class-duration-${(item.slug || item.name).toLowerCase().replace(/\s+/g, "-")}`}>{item.duration}</span>
                <span data-testid={`class-level-${(item.slug || item.name).toLowerCase().replace(/\s+/g, "-")}`}>{item.level}</span>
              </div>
            </div>
          </RevealBlock>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsSection = ({ testimonials }) => (
  <section className="section-shell overflow-hidden pt-0" data-testid="testimonials-section" id="testimonials">
    <div className="shell mb-8">
      <SectionIntro
        body="Members stay for the results and remember the environment. The experience is built to feel polished from the first click to the first rep."
        eyebrow="Member voice"
        testId="testimonials-section-intro"
        title="The club experience people talk about after the workout ends."
      />
    </div>

    <Marquee autoFill className="testimonial-marquee" gradient={false} speed={28}>
      {testimonials.map((item) => (
        <article className="testimonial-card" data-testid={`testimonial-card-${item.slug}`} key={item.slug}>
          <p className="text-2xl font-semibold leading-tight text-[#1A2525]" data-testid={`testimonial-quote-${item.slug}`}>
            “{item.quote}”
          </p>
          <p className="mt-4 text-sm tracking-[0.22em] text-[#758A8A]" data-testid={`testimonial-author-${item.slug}`}>
            {item.author}
          </p>
        </article>
      ))}
    </Marquee>
  </section>
);

const ContactSection = ({ clubInfo }) => (
  <section className="section-shell" data-testid="contact-section" id="membership">
    <div className="shell grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-14">
      <div className="space-y-8">
        <SectionIntro
          body="Start with a membership inquiry and let the Pulse Fitness team guide you to the right plan, coach, and training rhythm."
          eyebrow="Membership inquiry"
          testId="membership-section-intro"
          title="Tell us what you want to achieve and we’ll shape the right start."
        />

        <RevealBlock className="floating-panel space-y-5 p-6 md:p-8" delay={0.16}>
          <div className="space-y-2">
            <p className="eyebrow" data-testid="contact-us-heading-eyebrow">Direct contact</p>
            <h3 className="text-3xl font-semibold tracking-tight text-[#1A2525]" data-testid="contact-us-heading-title">
              Contact Us
            </h3>
            <p className="section-copy text-sm" data-testid="contact-us-heading-copy">
              Reach Pulse Fitness instantly through phone, WhatsApp, email, or Instagram.
            </p>
          </div>

          <div className="flex flex-wrap gap-3" data-testid="contact-us-icon-links">
            <a
              className="contact-icon-pill"
              data-testid="contact-us-phone-pill"
              href={`tel:${clubInfo.phone}`}
            >
              <Phone className="h-4 w-4" />
              <span>Call</span>
            </a>
            <a
              className="contact-icon-pill"
              data-testid="contact-us-whatsapp-pill"
              href={`https://wa.me/92${clubInfo.whatsapp.replace(/\D/g, "").replace(/^0/, "")}`}
              rel="noreferrer"
              target="_blank"
            >
              <WhatsappLogo className="h-4 w-4" />
              <span>WhatsApp</span>
            </a>
            <a
              className="contact-icon-pill"
              data-testid="contact-us-email-pill"
              href={`mailto:${clubInfo.email}`}
            >
              <Sparkle className="h-4 w-4" />
              <span>Email</span>
            </a>
            <a
              className="contact-icon-pill"
              data-testid="contact-us-instagram-pill"
              href={PULSE_INSTAGRAM_LINK}
              rel="noreferrer"
              target="_blank"
            >
              <InstagramLogo className="h-4 w-4" />
              <span>Instagram</span>
            </a>
          </div>
        </RevealBlock>

        <RevealBlock className="floating-panel space-y-6 p-6 md:p-8" delay={0.2}>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-3" data-testid="contact-location-card">
              <MapPin className="h-8 w-8 text-[#2D5A5A]" />
              <p className="eyebrow">Location</p>
              <a
                className="section-copy whitespace-pre-line text-[#1A2525] transition-colors duration-300 hover:text-[#2D5A5A]"
                data-testid="contact-location-link"
                href={PULSE_MAP_LINK}
                rel="noreferrer"
                target="_blank"
              >
                {clubInfo.location}
              </a>
            </div>
            <div className="space-y-3" data-testid="contact-hours-card">
              <ClockCountdown className="h-8 w-8 text-[#2D5A5A]" />
              <p className="eyebrow">Hours</p>
              <p className="section-copy text-[#1A2525]" data-testid="contact-hours-copy">{clubInfo.hours}</p>
            </div>
            <div className="space-y-3" data-testid="contact-phone-card">
              <Phone className="h-8 w-8 text-[#2D5A5A]" />
              <p className="eyebrow">Phone</p>
              <a
                className="section-copy text-[#1A2525] transition-colors duration-300 hover:text-[#2D5A5A]"
                data-testid="contact-phone-link"
                href={`tel:${clubInfo.phone}`}
              >
                {clubInfo.phone}
              </a>
            </div>
            <div className="space-y-3" data-testid="contact-whatsapp-card">
              <WhatsappLogo className="h-8 w-8 text-[#2D5A5A]" />
              <p className="eyebrow">WhatsApp</p>
              <a
                className="section-copy text-[#1A2525] transition-colors duration-300 hover:text-[#2D5A5A]"
                data-testid="contact-whatsapp-link"
                href={`https://wa.me/92${clubInfo.whatsapp.replace(/\D/g, "").replace(/^0/, "")}`}
                rel="noreferrer"
                target="_blank"
              >
                {clubInfo.whatsapp}
              </a>
            </div>
            <div className="space-y-3" data-testid="contact-email-card">
              <Sparkle className="h-8 w-8 text-[#2D5A5A]" />
              <p className="eyebrow">Email</p>
              <a
                className="section-copy break-all text-[#1A2525] transition-colors duration-300 hover:text-[#2D5A5A]"
                data-testid="contact-email-link"
                href={`mailto:${clubInfo.email}`}
              >
                {clubInfo.email}
              </a>
            </div>
            <div className="space-y-3" data-testid="contact-instagram-card">
              <InstagramLogo className="h-8 w-8 text-[#2D5A5A]" />
              <p className="eyebrow">Instagram</p>
              <a
                className="section-copy text-[#1A2525] transition-colors duration-300 hover:text-[#2D5A5A]"
                data-testid="contact-instagram-link"
                href={PULSE_INSTAGRAM_LINK}
                rel="noreferrer"
                target="_blank"
              >
                instagram.com/pulsefitnesspk
              </a>
            </div>
          </div>

          <a
            className="map-frame block transition-transform duration-300 hover:-translate-y-1"
            data-testid="contact-map-frame"
            href={PULSE_MAP_LINK}
            id="contact"
            rel="noreferrer"
            target="_blank"
          >
            <iframe
              className="h-full w-full"
              data-testid="contact-map-embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Phase%208%20DHA%20Karachi%20Pakistan&z=15&output=embed"
              title="Pulse Fitness location map"
            />
          </a>

          <a
            className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.18em] text-[#2D5A5A] transition-colors duration-300 hover:text-[#1F4242]"
            data-testid="contact-map-link"
            href={PULSE_MAP_LINK}
            rel="noreferrer"
            target="_blank"
          >
            Open on Google Maps
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </RevealBlock>
      </div>

      <RevealBlock className="floating-panel p-6 md:p-8 lg:p-10" delay={0.25}>
        <MembershipInquiryForm />
      </RevealBlock>
    </div>
  </section>
);

export const FooterSection = () => {
  const location = useLocation();
  const onHomePage = location.pathname === "/";

  const renderFooterLink = (item) => {
    if (item.href.startsWith("/")) {
      return (
        <Link
          className="nav-link"
          data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          key={item.href}
          to={item.href}
        >
          {item.label}
        </Link>
      );
    }

    if (onHomePage) {
      return (
        <a
          className="nav-link"
          data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          href={item.href}
          key={item.href}
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link
        className="nav-link"
        data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        key={item.href}
        to={`/${item.href}`}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <footer className="border-t border-[#DDE1D8] pb-10 pt-8" data-testid="site-footer">
      <div className="shell flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm tracking-[0.16em] text-[#758A8A]" data-testid="footer-brand-copy">
          Pulse Fitness • Premium training club • Karachi
        </p>
        <div className="flex flex-wrap gap-5 text-sm text-[#4A5D5D]" data-testid="footer-links-group">
          {pulseContent.navItems.map((item) => renderFooterLink(item))}
        </div>
      </div>
    </footer>
  );
};

export const PulseFitnessPage = () => {
  const [clubInfo, setClubInfo] = useState(pulseContent.clubInfo);
  const [clubInfoStatus, setClubInfoStatus] = useState("loading");

  useEffect(() => {
    axios
      .get(`${API}/club-info`)
      .then(({ data }) => {
        setClubInfo((current) => ({
          ...current,
          ...data,
          stats: data.stats?.length ? data.stats : current.stats,
        }));
        setClubInfoStatus("ready");
      })
      .catch(() => {
        setClubInfoStatus("error");
      });
  }, []);

  const classes = useMemo(() => {
    if (!clubInfo.featured_classes?.length) {
      return pulseContent.classes;
    }

    const classImageMap = pulseContent.classes.reduce((accumulator, classItem) => {
      accumulator[classItem.name] = classItem.image;
      return accumulator;
    }, {});

    return clubInfo.featured_classes.map((item, index) => ({
      slug: toKebabCase(item.name),
      name: item.name,
      focus: item.focus,
      duration: item.duration,
      level: item.level,
      image:
        classImageMap[item.name] ||
        (index % 2 === 0 ? pulseContent.media.classOne : pulseContent.media.classTwo),
    }));
  }, [clubInfo.featured_classes]);

  return (
    <main className="app-shell" data-testid="pulse-fitness-page">
      <SiteHeader navItems={pulseContent.navItems} />
      <HeroSection clubInfo={clubInfo} status={clubInfoStatus} />

      <section className="section-shell pt-0" data-testid="club-intro-section">
        <div className="shell grid gap-6 md:grid-cols-3">
          <RevealBlock className="floating-panel p-6" delay={0.05}>
            <FireSimple className="mb-4 h-8 w-8 text-[#2D5A5A]" />
            <p className="eyebrow" data-testid="club-intro-energy-eyebrow">Atmosphere</p>
            <p className="section-copy text-[#1A2525]" data-testid="club-intro-energy-copy">Natural light, soft tones, and premium finishes keep every visit calm and elevated.</p>
          </RevealBlock>

          <RevealBlock className="floating-panel p-6" delay={0.12}>
            <Barbell className="mb-4 h-8 w-8 text-[#2D5A5A]" />
            <p className="eyebrow" data-testid="club-intro-training-eyebrow">Training</p>
            <p className="section-copy text-[#1A2525]" data-testid="club-intro-training-copy">Structured strength, conditioning, and mobility systems guide members to steady progress.</p>
          </RevealBlock>

          <RevealBlock className="floating-panel p-6" delay={0.18}>
            <Sparkle className="mb-4 h-8 w-8 text-[#2D5A5A]" />
            <p className="eyebrow" data-testid="club-intro-premium-eyebrow">Membership</p>
            <p className="section-copy text-[#1A2525]" data-testid="club-intro-premium-copy">A refined club experience for people who want serious coaching with a softer visual mood.</p>
          </RevealBlock>
        </div>
      </section>

      <TourSection frames={pulseContent.immersiveFrames} />
      <ClassesSection classes={classes} />
      <TestimonialsSection testimonials={pulseContent.testimonials} />
      <ContactSection clubInfo={clubInfo} />
      <MobileBottomBar />
      <FooterSection />
    </main>
  );
};